import { Timestamp } from '@angular/fire/firestore';
import { IReportColumnDef } from '../../../shared/shared-models/reports/report-column.interface';
import {
  IReportDefinition,
  IReportMetadataItem,
  IReportSummaryItem,
  ReportCellValue,
} from '../../../shared/shared-models/reports/report-definition.interface';
import { ReportPageOrientation } from '../../../shared/shared-models/reports/report-page-orientation.enum';
import {
  formatReportCurrency,
  formatReportDateTime,
  formatReportQuantity,
} from '../../../shared/shared-utils/reports/report-format.utils';
import { buildReportFilename } from '../../../shared/shared-utils/reports/report-filename.utils';
import { orderReportColumnsBySelection } from '../../../shared/shared-utils/reports/report-column.utils';
import {
  IStockTake,
  IStockTakeFinalFigure,
  IStockTakeItemDetail,
  StockTakeItemStatus,
} from '../models';
import {
  STOCK_TAKE_SUMMARY_REPORT_COLUMNS,
  StockTakeReportColumnId,
} from '../models/stock-take-report-column';
import { formatStatus } from './stock-take-validation.utils';
import {
  sortStockTakeFinalFiguresForReport,
  STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID,
} from './stock-take-report-sort.utils';
import { isStockTakeIncludeDelCol } from './stock-take-variance.utils';

/**
 * Whether the PDF is a completed audit snapshot or a pre-process draft review.
 */
export enum StockTakeSummaryReportPhase {
  finalised = 'finalised',
  preProcess = 'preProcess',
}

/**
 * Options for building a stock take summary PDF definition.
 */
export interface IBuildStockTakeSummaryReportOptions {
  generatedAt?: Date;
  phase?: StockTakeSummaryReportPhase;
  selectedColumnIds?: string[];
  /** Row sort column — numeric columns highest to lowest; text columns Z–A; ties by stock code. */
  sortColumnId?: string;
}

/**
 * Column ids printed on stock take summary PDFs — all report columns except latest cost.
 * When includeDelCol is false, Del-Col columns are also omitted.
 */
export function getStockTakeSummaryPrintColumnIds(includeDelCol: boolean = true): string[] {
  return STOCK_TAKE_SUMMARY_REPORT_COLUMNS
    .filter((column: IReportColumnDef) => {
      if (column.id === StockTakeReportColumnId.latestCost) {
        return false;
      }
      if (!includeDelCol) {
        return (
          column.id !== StockTakeReportColumnId.delColQty
          && column.id !== StockTakeReportColumnId.delColMovement
        );
      }
      return true;
    })
    .map((column: IReportColumnDef) => column.id);
}

/**
 * Build a reusable PDF report definition from stock take summary rows.
 */
export function buildStockTakeSummaryReportDefinition(
  stockTake: IStockTake,
  figures: IStockTakeFinalFigure[],
  options: IBuildStockTakeSummaryReportOptions = {},
): IReportDefinition {
  const generatedAt = options.generatedAt ?? new Date();
  const phase = options.phase ?? StockTakeSummaryReportPhase.finalised;
  const includeDelCol = isStockTakeIncludeDelCol(stockTake.includeDelCol);
  const selectedColumnIds = options.selectedColumnIds ?? getStockTakeSummaryPrintColumnIds(includeDelCol);
  const columns = orderReportColumnsBySelection(STOCK_TAKE_SUMMARY_REPORT_COLUMNS, selectedColumnIds);
  if (columns.length === 0) {
    throw new Error('At least one report column must be selected');
  }

  const totalQtyVariance = figures.reduce(
    (sum: number, figure: IStockTakeFinalFigure) => sum + (figure.variance ?? figure.varianceQty ?? 0),
    0,
  );
  const totalRandVariance = figures.reduce(
    (sum: number, figure: IStockTakeFinalFigure) => sum + (figure.randVariance ?? 0),
    0,
  );

  const sortColumnId =
    options.sortColumnId?.trim() ? options.sortColumnId : STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID;
  const sortedFigures = sortStockTakeFinalFiguresForReport(figures, sortColumnId);

  const isPreProcess = phase === StockTakeSummaryReportPhase.preProcess;
  const filenameBase = isPreProcess
    ? `stock-take-pre-process-${stockTake.name}`
    : `stock-take-summary-${stockTake.name}`;

  return {
    title: isPreProcess
      ? `Stock Take Summary (Pre-Process) — ${stockTake.name}`
      : `Stock Take Summary — ${stockTake.name}`,
    subtitle: isPreProcess
      ? 'Draft review of current counted values — not yet finalised'
      : 'Read-only audit report of values captured at finalisation',
    filename: buildReportFilename(filenameBase, generatedAt),
    orientation: ReportPageOrientation.landscape,
    pageSize: 'A4',
    generatedAt,
    metadata: buildStockTakeReportMetadata(stockTake),
    summary: buildStockTakeReportSummary(figures.length, totalQtyVariance, totalRandVariance),
    columns,
    rows: sortedFigures.map((figure: IStockTakeFinalFigure) => buildStockTakeReportRow(figure, columns)),
    footerNote: isPreProcess
      ? 'Values shown are the current counted figures before processing. They are not a finalised snapshot and may change if stock movements update before finalisation.'
      : 'Values shown are the finalised snapshot stored when this stock take was processed. Live stock file values may differ.',
  };
}

/**
 * Map live counted/unique detail rows into the final-figure shape used by the PDF adapter.
 */
export function mapStockTakeItemDetailsToReportFigures(
  items: IStockTakeItemDetail[],
): IStockTakeFinalFigure[] {
  return items
    .filter(
      (item: IStockTakeItemDetail) =>
        item.status === StockTakeItemStatus.counted || item.status === StockTakeItemStatus.unique,
    )
    .map(
      (item: IStockTakeItemDetail): IStockTakeFinalFigure => ({
        id: item.id,
        code: item.code,
        varianceQty: item.variance,
        description: item.description,
        qtyCounted: item.qtyCounted,
        timesCounted: item.timesCounted,
        onhand: item.onhand,
        delColQty: item.delColQty,
        movement: item.movement,
        delColMovement: item.delColMovement,
        latestCost: item.latestCost,
        avCost: item.avCost,
        binLocation: item.binLocation,
        countedByEmail: item.countedByEmail,
        variance: item.variance,
        randVariance: item.randVariance,
      }),
    );
}

/**
 * Available columns for the stock take summary report.
 */
export function getStockTakeSummaryReportColumns(): IReportColumnDef[] {
  return [...STOCK_TAKE_SUMMARY_REPORT_COLUMNS];
}

function buildStockTakeReportMetadata(stockTake: IStockTake): IReportMetadataItem[] {
  return [
    { label: 'Status', value: formatStatus(stockTake.status) },
    { label: 'Assigned To', value: stockTake.userAssigned || '—' },
    { label: 'Del-Col', value: isStockTakeIncludeDelCol(stockTake.includeDelCol) ? 'Enabled' : 'Disabled' },
    { label: 'Start', value: formatTimestamp(stockTake.startTime) },
    { label: 'End', value: formatTimestamp(stockTake.endTime) },
    { label: 'Processed By', value: stockTake.processedByEmail || '—' },
  ];
}

function buildStockTakeReportSummary(
  itemCount: number,
  totalQtyVariance: number,
  totalRandVariance: number,
): IReportSummaryItem[] {
  return [
    { label: 'Items', value: String(itemCount) },
    { label: 'Qty Variance', value: formatReportQuantity(totalQtyVariance) },
    { label: 'Rand Variance', value: formatReportCurrency(totalRandVariance) },
  ];
}

function buildStockTakeReportRow(
  figure: IStockTakeFinalFigure,
  columns: IReportColumnDef[],
): Record<string, ReportCellValue> {
  const row: Record<string, ReportCellValue> = {};
  for (const column of columns) {
    row[column.id] = readFinalFigureValue(figure, column.id as StockTakeReportColumnId);
  }
  return row;
}

function readFinalFigureValue(
  figure: IStockTakeFinalFigure,
  columnId: StockTakeReportColumnId,
): ReportCellValue {
  switch (columnId) {
    case StockTakeReportColumnId.code:
      return figure.code;
    case StockTakeReportColumnId.description:
      return figure.description ?? '';
    case StockTakeReportColumnId.qtyCounted:
      return figure.qtyCounted ?? 0;
    case StockTakeReportColumnId.timesCounted:
      return figure.timesCounted ?? 0;
    case StockTakeReportColumnId.binLocation:
      return figure.binLocation ?? '';
    case StockTakeReportColumnId.delColQty:
      return figure.delColQty ?? 0;
    case StockTakeReportColumnId.onhand:
      return figure.onhand ?? 0;
    case StockTakeReportColumnId.movement:
      return figure.movement ?? 0;
    case StockTakeReportColumnId.delColMovement:
      return figure.delColMovement ?? 0;
    case StockTakeReportColumnId.variance:
      return figure.variance ?? figure.varianceQty ?? 0;
    case StockTakeReportColumnId.latestCost:
      return figure.latestCost ?? 0;
    case StockTakeReportColumnId.avCost:
      return figure.avCost ?? 0;
    case StockTakeReportColumnId.randVariance:
      return figure.randVariance ?? 0;
    case StockTakeReportColumnId.countedByEmail:
      return figure.countedByEmail ?? '';
    default:
      return '';
  }
}

function formatTimestamp(timestamp: Timestamp | null | undefined): string {
  if (!timestamp || typeof timestamp.toDate !== 'function') {
    return '—';
  }
  return formatReportDateTime(timestamp.toDate().getTime());
}
